<template>
  <v-card>
    <v-card-title>
      <span class="text-subtitle-1">Reporte de vendedoras</span>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        small
        tile
        class="mr-2"
        :loading="cargando"
        @click="cargarReporte()"
      >
        <v-icon left small>mdi-refresh</v-icon>
        Actualizar
      </v-btn>
      <v-btn icon small @click="$emit('cerrar')"><v-icon>mdi-close</v-icon></v-btn>
    </v-card-title>

    <v-card-text>
      <v-alert type="info" dense text class="mb-4">
        Solo se toman en cuenta las evaluaciones NI capturadas desde el <b>{{ fechaInicio }}</b> (sin eliminadas ni rechazadas).
        Genera el resumen con IA de cada vendedora; si recibe evaluaciones nuevas se marcará como desactualizado.
        Con el botón de WhatsApp se analizan sus conversaciones de Twilio de los últimos 7 días (Hallazgos Twilio IA).
      </v-alert>

      <v-row>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="search"
            label="Buscar vendedora o plantel"
            filled
            dense
            hide-details
            append-icon="mdi-magnify"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="escuela"
            :items="escuelas"
            label="Escuela"
            filled
            dense
            hide-details
          ></v-select>
        </v-col>
        <v-col cols="12" md="4" class="d-flex align-center">
          <v-switch
            v-model="soloConEvaluaciones"
            label="Solo con evaluaciones"
            hide-details
            dense
            class="mt-0"
          ></v-switch>
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="vendedorasFiltradas"
        :search="search"
        :loading="cargando"
        item-key="id_usuario"
        show-expand
        :expanded.sync="expandidas"
        class="elevation-0 mt-2"
        :mobile-breakpoint="100"
        dense
      >
        <template v-slot:item.nombre_completo="{ item }">
          <div class="py-1">
            <div>{{ item.nombre_completo }}</div>
            <div class="text-caption grey--text">{{ item.id_puesto == 18 ? 'Encargada' : 'Ventas' }}</div>
          </div>
        </template>

        <template v-slot:item.calificacion_promedio="{ item }">
          <v-chip small dark :color="colorCalificacionRetro( item.calificacion_promedio )">
            {{ item.calificacion_promedio !== null ? item.calificacion_promedio : '—' }}
          </v-chip>
        </template>

        <template v-slot:item.tendencia="{ item }">
          <div class="d-flex align-center">
            <v-icon small :color="tendencias[item.tendencia].color" class="mr-1">{{ tendencias[item.tendencia].icono }}</v-icon>
            <span class="text-caption mr-2">{{ tendencias[item.tendencia].texto }}</span>
            <v-sparkline
              v-if="item.calificaciones.length >= 2"
              :value="item.calificaciones"
              :color="tendencias[item.tendencia].color"
              line-width="3"
              padding="4"
              smooth
              style="width: 80px; height: 24px;"
            ></v-sparkline>
          </div>
        </template>

        <template v-slot:item.estado_resumen="{ item }">
          <div class="d-flex align-center">
            <v-chip v-if="!item.resumen_ia" x-small color="grey" dark>Sin generar</v-chip>
            <v-chip v-else-if="item.resumen_ia.desactualizado" x-small color="orange" dark>Desactualizado</v-chip>
            <v-chip v-else x-small color="green" dark>Al día</v-chip>
            <v-btn
              icon
              small
              color="deep-purple"
              class="ml-1"
              :title="item.resumen_ia ? 'Regenerar resumen IA' : 'Generar resumen IA'"
              :loading="generando === item.id_usuario"
              :disabled="!item.total_retros || (generando !== null && generando !== item.id_usuario)"
              @click="generar( item )"
            >
              <v-icon small>mdi-creation</v-icon>
            </v-btn>
          </div>
        </template>

        <template v-slot:item.hallazgos_twilio="{ item }">
          <div class="d-flex align-center py-1">
            <div>
              <v-chip v-if="!item.hallazgos_twilio" x-small color="grey" dark>Sin analizar</v-chip>
              <v-chip v-else-if="item.hallazgos_twilio.estado === 'procesando'" x-small color="blue" dark>Analizando…</v-chip>
              <v-chip v-else-if="item.hallazgos_twilio.estado === 'error'" x-small color="red" dark>Error</v-chip>
              <template v-else>
                <div class="text-caption text-no-wrap">
                  {{ item.hallazgos_twilio.conversaciones }} conv · {{ formatoMinutos( item.hallazgos_twilio.respuesta_promedio_min ) }}
                </div>
                <div class="text-caption grey--text text-no-wrap">
                  {{ item.hallazgos_twilio.pct_audios !== null ? item.hallazgos_twilio.pct_audios + '% audios' : 'sin audios' }}
                </div>
              </template>
            </div>
            <v-btn
              icon
              small
              color="green darken-1"
              class="ml-1"
              :title="item.hallazgos_twilio ? 'Regenerar hallazgos de WhatsApp' : 'Analizar conversaciones de WhatsApp'"
              :loading="!!item.hallazgos_twilio && item.hallazgos_twilio.estado === 'procesando'"
              @click="generarHallazgos( item )"
            >
              <v-icon small>mdi-whatsapp</v-icon>
            </v-btn>
          </div>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="py-4">
            <div v-if="!item.resumen_ia" class="grey--text">
              {{ item.total_retros ? 'Aún no se ha generado el resumen con IA.' : 'Sin evaluaciones para generar el resumen.' }}
            </div>

            <template v-else>
              <p class="mb-3">{{ item.resumen_ia.resumen }}</p>

              <v-row>
                <v-col cols="12" md="6">
                  <ListaPuntosIa :items="item.resumen_ia.fortalezas" tipo="fortaleza" titulo="Fortalezas" />
                </v-col>
                <v-col cols="12" md="6">
                  <ListaPuntosIa :items="item.resumen_ia.areas_oportunidad" tipo="area" titulo="Áreas de oportunidad" />
                </v-col>
              </v-row>

              <div class="mt-2">
                <span class="font-weight-bold">Tendencia: </span>{{ item.resumen_ia.tendencia_detalle }}
              </div>

              <div class="text-caption grey--text mt-2">
                Generado el {{ String(item.resumen_ia.fecha_generacion).replace('T', ' ').slice(0, 16) }}
                con {{ item.resumen_ia.total_retros }} evaluación(es).
              </div>
            </template>

            <v-divider class="my-4"></v-divider>

            <HallazgosTwilioDetalle
              :hallazgos="item.hallazgos_twilio"
              :id-usuario="item.id_usuario"
              :nombre="item.nombre_completo"
            />
          </td>
        </template>
      </v-data-table>
    </v-card-text>

    <Alerta v-if="respuestaAlerta" :parametros="parametros"/>
  </v-card>
</template>

<script>
import Alerta from '@/components/alertas/Alerta.vue'
import validarErrores from '@/mixins/validarErrores'
import HallazgosTwilioDetalle from '@/components/retroalimentacion/HallazgosTwilioDetalle.vue'
import ListaPuntosIa from '@/components/retroalimentacion/ListaPuntosIa.vue'
import { colorCalificacionRetro, formatoMinutosRetro } from '@/constants/retroalimentacion-comercial'

export default {
  name: 'ReporteRetroVendedoras',

  mixins: [ validarErrores ],

  components: {
    Alerta,
    HallazgosTwilioDetalle,
    ListaPuntosIa,
  },

  data: () => ({
    vendedoras: [],
    fechaInicio: '',
    cargando: false,
    generando: null,
    search: '',
    escuela: 0,
    escuelas: [
      { text: 'Todas', value: 0 },
      { text: 'INBI' , value: 1 },
      { text: 'FAST' , value: 2 },
    ],
    soloConEvaluaciones: false,
    expandidas: [],
    headers: [
      { text: 'Vendedora'   , value: 'nombre_completo' },
      { text: 'Plantel'     , value: 'plantel' },
      { text: 'Evaluaciones', value: 'total_retros' },
      { text: 'Calificación', value: 'calificacion_promedio' },
      { text: 'Tendencia'   , value: 'tendencia', sortable: false },
      { text: 'Resumen IA'  , value: 'estado_resumen', sortable: false },
      { text: 'Hallazgos Twilio IA', value: 'hallazgos_twilio', sortable: false },
      { text: ''            , value: 'data-table-expand' },
    ],
    timerHallazgos: null,
    tendencias: {
      mejora   : { icono: 'mdi-trending-up'     , color: 'green' , texto: 'Mejora' },
      empeora  : { icono: 'mdi-trending-down'   , color: 'red'   , texto: 'Empeora' },
      estable  : { icono: 'mdi-trending-neutral', color: 'blue'  , texto: 'Estable' },
      sin_datos: { icono: 'mdi-minus'           , color: 'grey'  , texto: 'Sin datos' },
    },
  }),

  computed: {
    vendedorasFiltradas () {
      return this.vendedoras.filter(v =>
        (!this.soloConEvaluaciones || v.total_retros > 0) &&
        (!this.escuela || v.escuela === this.escuela)
      )
    },
  },

  created () {
    this.cargarReporte()
  },

  beforeDestroy () {
    clearTimeout(this.timerHallazgos)
  },

  methods: {
    colorCalificacionRetro,

    cargarReporte () {
      this.cargando = true
      return this.$http.get('retroalimentacion.reporte.vendedoras').then(response => {
        this.fechaInicio = response.data.fecha_inicio
        this.vendedoras = response.data.vendedoras
        this.programarEstadoHallazgos()
      }).catch(error => {
        this.validarError(error)
      }).finally(() => { this.cargando = false })
    },

    generar (item) {
      this.generando = item.id_usuario
      return this.$http.post('retroalimentacion.reporte.generar', { id_usuario: item.id_usuario }).then(response => {
        item.resumen_ia = response.data
        if (!this.expandidas.includes(item)) this.expandidas.push(item)
      }).catch(error => {
        this.validarError(error)
      }).finally(() => { this.generando = null })
    },

    generarHallazgos (item) {
      return this.$http.post('retroalimentacion.hallazgos.generar', { id_usuario: item.id_usuario }).then(response => {
        item.hallazgos_twilio = response.data
        this.programarEstadoHallazgos()
      }).catch(error => {
        this.validarError(error)
      })
    },

    programarEstadoHallazgos () {
      clearTimeout(this.timerHallazgos)
      const pendientes = this.vendedoras.filter(v => v.hallazgos_twilio && v.hallazgos_twilio.estado === 'procesando')
      if (!pendientes.length) return
      this.timerHallazgos = setTimeout(() => {
        const ids = pendientes.map(v => v.id_usuario).join(',')
        this.$http.get('retroalimentacion.hallazgos.estado', { params: { ids } }).then(response => {
          response.data.forEach(r => {
            const vendedora = this.vendedoras.find(v => v.id_usuario == r.id_usuario)
            if (vendedora) vendedora.hallazgos_twilio = r.hallazgos_twilio
          })
        }).catch(() => {}).finally(() => { this.programarEstadoHallazgos() })
      }, 8000)
    },

    formatoMinutos: formatoMinutosRetro,
  },
}
</script>
